<template>
  <div class="user-menu-container">
    <!-- Authenticated -->
    <div
      v-if="auth.isAuthenticated.value"
      class="nav-dropdown-container"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
    >
      <button class="user-menu-btn" :class="{ show: open }" @click="open = !open">
        <div class="user-avatar">{{ avatarInitial }}</div>
        <span class="user-menu-name">{{ auth.displayName.value }}</span>
        <span v-if="environmentLabel" class="env-pill" :class="envPillClass">{{ environmentLabel }}</span>
        <i class="fas fa-chevron-down chevron-icon"></i>
      </button>

      <div class="dropdown-menu dropdown-menu-right" :class="{ show: open }">
        <!-- Environment / Network Header -->
        <div class="dropdown-header-section">
          <div class="dropdown-header-env">
            <i :class="envIcon" class="dropdown-header-env-icon"></i>
            <div>
              <span class="dropdown-header-env-label">{{ environmentLabel }}</span>
              <span v-if="walletConnected" class="dropdown-header-wallet">
                <i class="fas fa-wallet"></i> {{ walletStore.shortAddress }}
              </span>
              <button
                v-if="isEmulator"
                type="button"
                class="dropdown-reset-btn"
                :disabled="resettingEmulator"
                @click="handleResetEmulatorUserWallet"
              >
                <i class="fas fa-redo-alt"></i>
                {{ resettingEmulator ? 'Resetting...' : 'Reset Emulated User Wallet' }}
              </button>
            </div>
          </div>
          <div v-if="walletConnected && !isEmulator" class="dropdown-header-balance">
            {{ walletStore.balanceADA.toFixed(2) }} <small>ADA</small>
          </div>
        </div>

        <div class="dropdown-divider"></div>

        <router-link to="/account" class="dropdown-item" @click="open = false">
          <i class="fas fa-user mr-2"></i>Profile
        </router-link>
        <router-link to="/account/settings" class="dropdown-item" @click="open = false">
          <i class="fas fa-cog mr-2"></i>Settings
        </router-link>
        <router-link to="/account/security" class="dropdown-item" @click="open = false">
          <i class="fas fa-shield-alt mr-2"></i>Security
        </router-link>
        <div class="dropdown-divider"></div>
        <button v-if="walletConnected" class="dropdown-item" @click="handleDisconnect">
          <i class="fas fa-unlink mr-2"></i>Disconnect Wallet
        </button>
        <button class="dropdown-item text-danger" @click="handleLogout">
          <i class="fas fa-sign-out-alt mr-2"></i>Logout
        </button>
      </div>
    </div>

    <!-- Not Authenticated -->
    <div
      v-else
      class="nav-dropdown-container"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
    >
      <button class="account-btn" :class="{ show: open }" @click="open = !open">
        <i class="fas fa-user mr-1"></i>
        Account
        <i class="fas fa-chevron-down chevron-icon"></i>
      </button>

      <div class="dropdown-menu dropdown-menu-right" :class="{ show: open }">
        <router-link to="/login" class="dropdown-item" @click="open = false">
          <i class="fas fa-sign-in-alt mr-2"></i>Sign In
        </router-link>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useAuth } from '@/composables/useAuth'
import { useWalletStore } from '@/stores/wallet'
import { useCredentialStore } from '@/stores/credentials'
import { useSettingsStore } from '@/stores/settings'
import { resetCredentialDeposit } from '@/services/api/contracts/credentials'

const router = useRouter()
const auth = useAuth()
const walletStore = useWalletStore()
const credStore = useCredentialStore()
const settings = useSettingsStore()
const open = ref(false)
const resettingEmulator = ref(false)
const hoverEnabled = ref(typeof window !== 'undefined' ? window.innerWidth >= 992 : true)
let hoverMediaQuery: MediaQueryList | null = null

const walletConnected = computed(() => walletStore.isConnected)
const isEmulator = computed(() => settings.emulatorMode)

const environmentLabel = computed(() => {
  if (isEmulator.value) return 'Emulator'
  if (walletConnected.value) return walletStore.networkName
  return ''
})

const envPillClass = computed(() => {
  if (isEmulator.value) return 'env-pill--emulator'
  if (walletStore.networkId === 1) return 'env-pill--mainnet'
  return 'env-pill--testnet'
})

const envIcon = computed(() => {
  if (isEmulator.value) return 'fas fa-microchip'
  if (walletStore.networkId === 1) return 'fas fa-globe'
  return 'fas fa-flask'
})

const avatarInitial = computed(() => {
  const name = auth.displayName.value
  if (!name || name === 'Unknown') return '?'
  return name[0].toUpperCase()
})

function handleMouseEnter() {
  if (!hoverEnabled.value) return
  open.value = true
}

function handleMouseLeave() {
  if (!hoverEnabled.value) return
  open.value = false
}

function handleDisconnect() {
  open.value = false
  walletStore.disconnect()
}

async function handleLogout() {
  open.value = false
  await auth.logout()
  walletStore.disconnect()
  router.push('/login')
}

async function handleResetEmulatorUserWallet() {
  if (!isEmulator.value || resettingEmulator.value) return

  resettingEmulator.value = true
  try {
    const pkh = credStore.userPkh || walletStore.getPaymentKeyHash() || undefined
    const result = await resetCredentialDeposit(pkh || undefined)
    if (!result.success) {
      console.error('[UserMenu] Emulator credential reset failed:', result.error)
      return
    }
    credStore.$reset()
    open.value = false
  } catch (err) {
    console.error('[UserMenu] Emulator credential reset error:', err)
  } finally {
    resettingEmulator.value = false
  }
}

function handleHoverMediaChange(event: MediaQueryListEvent) {
  hoverEnabled.value = event.matches
}

onMounted(() => {
  if (typeof window === 'undefined') return
  hoverMediaQuery = window.matchMedia('(min-width: 992px)')
  hoverEnabled.value = hoverMediaQuery.matches
  hoverMediaQuery.addEventListener('change', handleHoverMediaChange)
})

onBeforeUnmount(() => {
  hoverMediaQuery?.removeEventListener('change', handleHoverMediaChange)
})
</script>

<style scoped src="../../assets/css/extracted/components/common/UserMenu.css"></style>
